import * as Sentry from "@sentry/react";

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const SENTRY_ENABLED = process.env.REACT_APP_SENTRY_ENABLED;
const SENTRY_REPLAY_SESSION_SAMPLE_RATE = 1;
const SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE = 1;

export interface SentryCaptureContext {
  action?: string;
  tags?: Record<string, unknown>;
  params?: Record<string, unknown>;
}

export interface MonitoringBreadcrumb {
  category?: string;
  action?: string;
  message?: string;
  metadata?: Record<string, unknown>;
}

export interface MonitoringUser {
  id?: string;
  email?: string;
  username?: string;
}

const parseSampleRate = (
  value: string | undefined,
  fallback: number
): number => {
  if (value === undefined || value.trim() === "") return fallback;

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;

  return Math.min(1, Math.max(0, parsed));
};

const isEnabledByConfig = (dsn = SENTRY_DSN): boolean => {
  if (!dsn || dsn.trim() === "") return false;
  return SENTRY_ENABLED !== "false";
};

const normalizeTagValue = (value: unknown): string => {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value).slice(0, 200);
  } catch {
    return "[Unserializable]";
  }
};

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  return new Error(String(error));
};

const getBrowserPathname = (): string => {
  if (typeof window === "undefined") return "";
  return window.location.pathname;
};

export const isSentryReplayEnabled = (pathname = getBrowserPathname()) =>
  !pathname.startsWith("/render");

export const getSentryConfig = (
  dsn = SENTRY_DSN,
  pathname = getBrowserPathname()
) => {
  const replayEnabled = isSentryReplayEnabled(pathname);

  return {
    dsn,
    enabled: isEnabledByConfig(dsn),
    environment:
      process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    release:
      process.env.REACT_APP_SENTRY_RELEASE ||
      process.env.REACT_APP_GIT_REVISION,
    tracesSampleRate: parseSampleRate(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
      0
    ),
    replayEnabled,
    replaysSessionSampleRate: replayEnabled
      ? parseSampleRate(
          process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
          SENTRY_REPLAY_SESSION_SAMPLE_RATE
        )
      : 0,
    replaysOnErrorSampleRate: replayEnabled
      ? parseSampleRate(
          process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
          SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE
        )
      : 0
  };
};

const initializeSentry = (): boolean => {
  const config = getSentryConfig();
  if (!config.enabled) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "Sentry: No DSN configured. Sentry is disabled. " +
          "Set REACT_APP_SENTRY_DSN to enable error monitoring."
      );
    }
    return false;
  }

  try {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,
      dataCollection: {},
      // Enables the Trace Metrics API (Sentry.metrics.*) used for analytics
      // event tracking under the sentry metrics provider.
      enableMetrics: true,
      tracesSampleRate: config.tracesSampleRate,
      replaysSessionSampleRate: config.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
      integrations: integrations => {
        const configuredIntegrations = integrations.filter(
          integration => integration.name !== "GlobalHandlers"
        );

        if (!config.replayEnabled) return configuredIntegrations;

        return [
          ...configuredIntegrations,
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            _experiments: {
              recordCrossOriginIframes: true
            }
          })
        ];
      }
    });

    console.info("Sentry: Successfully initialized");
    return true;
  } catch (error) {
    console.error("Sentry: Failed to initialize", error);
    return false;
  }
};

export const isSentryActive = initializeSentry();

export const captureException = (
  error: unknown,
  context: SentryCaptureContext = {}
): void => {
  if (!isSentryActive) return;

  try {
    Sentry.withScope(scope => {
      if (context.action) {
        scope.setTag("action", context.action);
      }

      Object.entries(context.tags || {}).forEach(([key, value]) => {
        scope.setTag(key, normalizeTagValue(value));
      });

      if (context.params && Object.keys(context.params).length > 0) {
        scope.setContext("params", context.params);
      }

      Sentry.captureException(normalizeError(error));
    });
  } catch (captureError) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Sentry: Failed to capture exception", captureError);
    }
  }
};

export const addBreadcrumb = (breadcrumb: MonitoringBreadcrumb): void => {
  if (!isSentryActive) return;

  try {
    Sentry.addBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message || breadcrumb.action,
      data: breadcrumb.metadata,
      level: "info"
    });
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Sentry: Failed to add breadcrumb", error);
    }
  }
};

export const setUser = (user: MonitoringUser | null): void => {
  if (!isSentryActive) return;

  try {
    Sentry.setUser(user);
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Sentry: Failed to set user", error);
    }
  }
};

export default {
  isActive: isSentryActive,
  captureException,
  addBreadcrumb,
  setUser
};
